"use client"

import { useState, useMemo } from "react"
import { RotateCcw, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2 } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { eurosEn } from "./formato"

const T = {
  es: {
    datosMensuales: "Datos mensuales",
    limpiar: "Limpiar",
    ingresos: "Ingresos mensuales (€)",
    gastosMensuales: "Gastos mensuales",
    materiaPrima: "Coste de materia prima (€)",
    personal: "Coste de personal (€)",
    alquiler: "Alquiler (€)",
    suministros: "Suministros (€)",
    otrosGastos: "Otros gastos (€)",
    resultados: "Resultados",
    beneficioNeto: "Beneficio neto mensual",
    margenNeto: "Margen neto",
    desglose: "Desglose de gastos",
    leyenda: {
      materiaPrima: "Materia prima",
      personal: "Personal",
      alquiler: "Alquiler",
      suministros: "Suministros",
      otros: "Otros",
    },
    excelente: "Excelente",
    normal: "Normal",
    alto: "Alto",
    ratiosClave: "Ratios clave",
    ratioMateriaPrima: "Ratio materia prima",
    ratioPersonal: "Ratio personal",
    costePrimo: "Coste primo",
    ideal: (min: number, max: number) => `Ideal: ${min}-${max}%`,
    recomendaciones: "Recomendaciones",
    recMateriaPrima:
      "Tu ratio de materia prima es alto. Revisa escandallos, negocia con proveedores o ajusta precios de carta.",
    recPersonal: "El coste de personal supera el 35%. Optimiza turnos y valora la productividad por empleado.",
    recCostePrimo:
      "Tu coste primo supera el 65%. Queda poco margen para cubrir el resto de gastos y generar beneficio.",
    recPerdidas: "Estás operando con pérdidas. Necesitas aumentar ingresos o reducir gastos urgentemente.",
    recSaludable: "Tus ratios están dentro de los rangos saludables. ¡Sigue así!",
    recExcelente: "Tu margen neto es excelente. Considera reinvertir en el negocio para crecer.",
  },
  en: {
    datosMensuales: "Monthly figures",
    limpiar: "Reset",
    ingresos: "Monthly revenue (€)",
    gastosMensuales: "Monthly costs",
    materiaPrima: "Food and drink cost (€)",
    personal: "Staff cost (€)",
    alquiler: "Rent (€)",
    suministros: "Utilities (€)",
    otrosGastos: "Other costs (€)",
    resultados: "Results",
    beneficioNeto: "Monthly net profit",
    margenNeto: "Net margin",
    desglose: "Cost breakdown",
    leyenda: {
      materiaPrima: "Food and drink",
      personal: "Staff",
      alquiler: "Rent",
      suministros: "Utilities",
      otros: "Other",
    },
    excelente: "Excellent",
    normal: "Normal",
    alto: "High",
    ratiosClave: "Key ratios",
    ratioMateriaPrima: "Food cost ratio",
    ratioPersonal: "Labour cost ratio",
    costePrimo: "Prime cost",
    ideal: (min: number, max: number) => `Target: ${min}-${max}%`,
    recomendaciones: "Recommendations",
    recMateriaPrima:
      "Your food cost ratio is high. Review your recipe costings, negotiate with suppliers or adjust your menu prices.",
    recPersonal: "Staff costs are above 35% of revenue. Tighten up your rotas and look at productivity per employee.",
    recCostePrimo:
      "Your prime cost is above 65%. That leaves little room to cover everything else and still make a profit.",
    recPerdidas: "You are running at a loss. You need to increase revenue or cut costs urgently.",
    recSaludable: "Your ratios are within healthy ranges. Keep it up!",
    recExcelente: "Your net margin is excellent. Consider reinvesting in the business to grow.",
  },
}

const defaultValues = {
  ingresos: 45000,
  materiaPrima: 13500,
  personal: 13000,
  alquiler: 4000,
  suministros: 2500,
  otrosGastos: 3000,
}

export default function RentabilidadCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const eurRedondo = (n: number) =>
    idioma === "en"
      ? eurosEn(n, 0)
      : `${n.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€`
  const eur = (n: number) =>
    idioma === "en"
      ? `€${n.toLocaleString("en-GB", { maximumFractionDigits: 2 })}`
      : `${n.toLocaleString("es-ES")}€`
  const [ingresos, setIngresos] = useState(defaultValues.ingresos)
  const [materiaPrima, setMateriaPrima] = useState(defaultValues.materiaPrima)
  const [personal, setPersonal] = useState(defaultValues.personal)
  const [alquiler, setAlquiler] = useState(defaultValues.alquiler)
  const [suministros, setSuministros] = useState(defaultValues.suministros)
  const [otrosGastos, setOtrosGastos] = useState(defaultValues.otrosGastos)

  const results = useMemo(() => {
    const totalGastos = materiaPrima + personal + alquiler + suministros + otrosGastos
    const beneficioNeto = ingresos - totalGastos
    const margenNeto = ingresos > 0 ? (beneficioNeto / ingresos) * 100 : 0
    const ratioMateriaPrima = ingresos > 0 ? (materiaPrima / ingresos) * 100 : 0
    const ratioPersonal = ingresos > 0 ? (personal / ingresos) * 100 : 0
    const costePrimo =
      ingresos > 0 ? ((materiaPrima + personal) / ingresos) * 100 : 0

    return {
      totalGastos,
      beneficioNeto,
      margenNeto,
      ratioMateriaPrima,
      ratioPersonal,
      costePrimo,
    }
  }, [ingresos, materiaPrima, personal, alquiler, suministros, otrosGastos])

  const reset = () => {
    setIngresos(defaultValues.ingresos)
    setMateriaPrima(defaultValues.materiaPrima)
    setPersonal(defaultValues.personal)
    setAlquiler(defaultValues.alquiler)
    setSuministros(defaultValues.suministros)
    setOtrosGastos(defaultValues.otrosGastos)
  }

  // Cost breakdown for visual bar
  const breakdown = useMemo(() => {
    const total = results.totalGastos || 1
    return [
      { label: t.leyenda.materiaPrima, value: materiaPrima, color: "bg-orange-500", pct: (materiaPrima / total) * 100 },
      { label: t.leyenda.personal, value: personal, color: "bg-violet-500", pct: (personal / total) * 100 },
      { label: t.leyenda.alquiler, value: alquiler, color: "bg-sky-500", pct: (alquiler / total) * 100 },
      { label: t.leyenda.suministros, value: suministros, color: "bg-amber-500", pct: (suministros / total) * 100 },
      { label: t.leyenda.otros, value: otrosGastos, color: "bg-zinc-400", pct: (otrosGastos / total) * 100 },
    ]
  }, [materiaPrima, personal, alquiler, suministros, otrosGastos, results.totalGastos, t])

  const getRatioBenchmark = (value: number, min: number, max: number) => {
    if (value <= min) return { label: t.excelente, color: "text-emerald-600", bg: "bg-emerald-50", icon: CheckCircle2 }
    if (value <= max) return { label: t.normal, color: "text-amber-600", bg: "bg-amber-50", icon: AlertTriangle }
    return { label: t.alto, color: "text-red-600", bg: "bg-red-50", icon: TrendingUp }
  }

  const inputClass =
    "w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent transition-shadow"

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.datosMensuales}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.ingresos}
            </label>
            <input
              type="number"
              value={ingresos || ""}
              onChange={(e) => setIngresos(parseFloat(e.target.value) || 0)}
              placeholder="0"
              min="0"
              step="any"
              className={inputClass}
            />
          </div>

          <div className="border-t border-zinc-200 pt-6">
            <p className="text-sm font-medium text-zinc-500 mb-4">{t.gastosMensuales}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                  {t.materiaPrima}
                </label>
                <input
                  type="number"
                  value={materiaPrima || ""}
                  onChange={(e) => setMateriaPrima(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  min="0"
                  step="any"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                  {t.personal}
                </label>
                <input
                  type="number"
                  value={personal || ""}
                  onChange={(e) => setPersonal(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  min="0"
                  step="any"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                  {t.alquiler}
                </label>
                <input
                  type="number"
                  value={alquiler || ""}
                  onChange={(e) => setAlquiler(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  min="0"
                  step="any"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                  {t.suministros}
                </label>
                <input
                  type="number"
                  value={suministros || ""}
                  onChange={(e) => setSuministros(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  min="0"
                  step="any"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                  {t.otrosGastos}
                </label>
                <input
                  type="number"
                  value={otrosGastos || ""}
                  onChange={(e) => setOtrosGastos(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  min="0"
                  step="any"
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.resultados}</h2>

          {/* Beneficio neto */}
          <div
            className={`rounded-2xl p-6 text-center ${results.beneficioNeto >= 0 ? "bg-emerald-50" : "bg-red-50"}`}
          >
            <p className="text-sm font-medium text-zinc-600 mb-1">{t.beneficioNeto}</p>
            <p
              className={`text-4xl font-bold ${results.beneficioNeto >= 0 ? "text-emerald-600" : "text-red-600"}`}
            >
              {results.beneficioNeto >= 0 ? "+" : ""}
              {eurRedondo(results.beneficioNeto)}
            </p>
            <p className="text-sm text-zinc-500 mt-1">
              {t.margenNeto}: {results.margenNeto.toFixed(1)}%
            </p>
          </div>

          {/* Cost breakdown bar */}
          <div>
            <p className="text-sm font-medium text-zinc-700 mb-3">
              {t.desglose} ({eur(results.totalGastos)})
            </p>
            <div className="flex rounded-full h-5 overflow-hidden">
              {breakdown.map((item, i) =>
                item.value > 0 ? (
                  <div
                    key={i}
                    className={`${item.color} transition-all duration-300`}
                    style={{ width: `${item.pct}%` }}
                    title={`${item.label}: ${eur(item.value)}`}
                  />
                ) : null
              )}
            </div>
            <div className="flex flex-wrap gap-3 mt-3">
              {breakdown.map((item, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs text-zinc-600">
                  <div className={`w-2.5 h-2.5 rounded-full ${item.color}`} />
                  {item.label} ({item.pct.toFixed(0)}%)
                </div>
              ))}
            </div>
          </div>

          {/* Ratios */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-zinc-700">{t.ratiosClave}</p>

            {[
              {
                label: t.ratioMateriaPrima,
                value: results.ratioMateriaPrima,
                benchmark: t.ideal(25, 35),
                min: 25,
                max: 35,
              },
              {
                label: t.ratioPersonal,
                value: results.ratioPersonal,
                benchmark: t.ideal(25, 35),
                min: 25,
                max: 35,
              },
              {
                label: t.costePrimo,
                value: results.costePrimo,
                benchmark: t.ideal(55, 65),
                min: 55,
                max: 65,
              },
            ].map((ratio, i) => {
              const bm = getRatioBenchmark(ratio.value, ratio.min, ratio.max)
              const BmIcon = bm.icon
              return (
                <div
                  key={i}
                  className={`flex items-center justify-between p-3 rounded-xl ${bm.bg}`}
                >
                  <div>
                    <p className="text-sm font-medium text-zinc-900">{ratio.label}</p>
                    <p className="text-xs text-zinc-500">{ratio.benchmark}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-lg font-bold ${bm.color}`}>
                      {ratio.value.toFixed(1)}%
                    </span>
                    <BmIcon className={`w-4 h-4 ${bm.color}`} />
                  </div>
                </div>
              )
            })}
          </div>

          {/* Recommendations */}
          <div className="border-t border-zinc-200 pt-4">
            <p className="text-sm font-medium text-zinc-700 mb-2">{t.recomendaciones}</p>
            <div className="space-y-2 text-sm text-zinc-600">
              {results.ratioMateriaPrima > 35 && (
                <div className="flex items-start gap-2">
                  <TrendingDown className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <p>
                    {t.recMateriaPrima}
                  </p>
                </div>
              )}
              {results.ratioPersonal > 35 && (
                <div className="flex items-start gap-2">
                  <TrendingDown className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <p>
                    {t.recPersonal}
                  </p>
                </div>
              )}
              {results.costePrimo > 65 && (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <p>
                    {t.recCostePrimo}
                  </p>
                </div>
              )}
              {results.margenNeto < 0 && (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <p>
                    {t.recPerdidas}
                  </p>
                </div>
              )}
              {results.margenNeto >= 5 &&
                results.margenNeto <= 15 &&
                results.costePrimo <= 65 && (
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    <p>
                      {t.recSaludable}
                    </p>
                  </div>
                )}
              {results.margenNeto > 15 && (
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <p>
                    {t.recExcelente}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
